import { AgentResponse } from 'apis/business/agent/types';
import { VerifyCodeResponse } from 'apis/auth/types';
import * as urls from 'routes/config';
import http from 'utils/http';
import { CacheKeys } from 'utils/cacheKeys';
import { setCookie } from 'utils/helperFunctions';
import { NavigateFunction } from 'react-router-dom';

type LoginSessionContext = {
  navigate: NavigateFunction;
  setSelectedBusiness: (id: number) => void;
  setSelectedLocation: (id: number) => void;
  returnTo?: string | null;
};

export function decodeNetsapiensSession(encoded: string): VerifyCodeResponse {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
}

export async function completeLoginSession(data: VerifyCodeResponse, context: LoginSessionContext) {
  const { navigate, setSelectedBusiness, setSelectedLocation, returnTo } = context;

  setCookie(CacheKeys.isAuthenticated, 'true');

  const userRole = data.role;
  const userStatus = ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(userRole)
    ? 'active'
    : data?.businesses?.[0]?.account_status;

  if (data?.sso?.token) {
    setCookie(CacheKeys.ssoSupportToken, data.sso.token);
  }
  setCookie(CacheKeys.userStatus, userStatus);

  if (returnTo) {
    const redirectUrl = new URL('https://supportkickcall.featurebase.app/api/v1/auth/access/jwt');
    redirectUrl.searchParams.set('jwt', data.sso?.token as string);
    redirectUrl.searchParams.set('return_to', returnTo);
    window.location.href = redirectUrl.toString();
    return;
  }

  if (data?.meta?.account_status === 'inactive' || data?.meta?.account_status === 'archived') {
    navigate('/access-denied');
    return;
  }

  if (data?.meta?.account_status === 'past_due') {
    navigate('/agency/billing');
    return;
  }

  if (['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(data.role)) {
    navigate(urls.agency.base);
    return;
  }

  const businesses = data.businesses;
  const locations = data.locations;
  if (!businesses?.length || !locations?.length) {
    navigate('/');
    return;
  }

  const locationId = locations[0].id;
  setSelectedBusiness(businesses[0].id);
  setSelectedLocation(locations[0].id);
  localStorage.setItem(CacheKeys.businessIds, JSON.stringify(businesses.map((item) => item.id)));
  localStorage.setItem(CacheKeys.locationIds, JSON.stringify(locations.map((item) => item.id)));

  if (['inactive', 'archived'].includes(userStatus ?? '')) {
    navigate('/access-denied');
    return;
  }

  const response = await http.get(`business/locations/${locationId}/agents`);
  if (response.data.length) {
    const ids = response.data.map((item: AgentResponse) => item.id);
    localStorage.setItem(CacheKeys.agentIds, JSON.stringify(ids));
    navigate(`${urls.agentBase(locations[0].id, response.data[0].id)}/${urls.home.performance}`);
  } else {
    navigate('/');
  }
}
